import React from 'react';
import { Link, usePage } from '@inertiajs/react';
import { Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCheck } from '@fortawesome/pro-regular-svg-icons';
import PlCategory from '../Data/PlCategory';
import { Role } from '../Data/Role';

interface Props {
	valuationId: number;
	pageName: string;
	pageIndex?: string | number;
}

interface Page {
	name: string;
	label: string;
}

const SideNav = ({ valuationId, pageName, pageIndex }: Props) => {
	const { navPlCategories, user, completedSteps, valuationAgencyName, valuationEffectiveDate } = usePage<any>().props;
	const completed: string[] = completedSteps ?? [];

	const url = `/valuation-form/${valuationId}`;
	const pages: Page[] = [
		{
			name: 'basics',
			label: 'Basics',
		},
		{
			name: 'ownership',
			label: 'Ownership',
		},
		{
			name: 'employees',
			label: 'Employees',
		},
		{
			name: 'accounts',
			label: 'Top Ten Accounts',
		},
		{
			name: 'pc-carriers',
			label: 'Top 5 P&C Carriers',
		},
		{
			name: 'lh-carriers',
			label: 'Top 5 L&H Carriers',
		},
		{
			name: 'producer-bobs',
			label: 'Producer Books of Business',
		},
		{
			name: 'non-recurring-revenues',
			label: 'Non-recurring Revenues',
		},
		{
			name: 'non-recurring-expenses',
			label: 'Non-recurring Expenses',
		},
		{
			name: 'agency-information',
			label: 'Agency Information',
		},
		{
			name: 'carrier-loss-ratios',
			label: 'Loss Ratio By Carrier',
		},
		{
			name: 'carrier-retention-rates',
			label: 'Retention Rate By Carrier',
		},
	];

	return (
		<div className='side-nav-container pb-8' style={{ paddingTop: '5rem' }}>
			{valuationAgencyName && (
				<div className='px-3 mb-5'>
					<div className='text-white fw-bold text-uppercase'>{valuationAgencyName}</div>
					{valuationEffectiveDate && (
						<small className='text-dkBlueGrey'>
							As of {new Date(valuationEffectiveDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
						</small>
					)}
				</div>
			)}
			<hr className='border-dkBlueGrey' />
			<span className='h4 fw-bold text-dkBlueGrey px-3'>Form Sections</span>
		<Nav className='side-nav flex-column'>
			{pages.map((page: Page) => (
				<Nav.Link
					as={Link}
					key={page.name}
					active={pageName === page.name}
					href={`${url}/${page.name}`}
					className='d-flex align-items-center gap-2'
				>
					<FontAwesomeIcon
						icon={completed.includes(page.name) ? faCheck : faStar}
						size='lg'
					/>
					{page.label}
				</Nav.Link>
			))}
			<hr className='border-dkBlueGrey' />

			<Nav.Link
				as={Link}
				active={pageName === 'pl-statement-upload'}
				href={`${url}/pl-statement-upload`}
				className='h4 py-2 px-3 fw-bold'
				style={{fontSize: '1rem'}}
			>
				Import P&L Statements
			</Nav.Link>

			<hr className='border-dkBlueGrey' />

			<Nav.Link
				as={Link}
				active={pageName === 'upload-agency-data'}
				href={`${url}/upload-agency-data`}
				className='h4 py-2 px-3 fw-bold'
				style={{fontSize: '1rem'}}
			>
				Upload Agency Data
			</Nav.Link>

			{user.role === Role.admin && (
				<>
					<hr className='border-dkBlueGrey' />

					<span className='h4 fw-bold text-dkBlueGrey px-3'>Net Revenues</span>
					{navPlCategories?.netRevenues.map((category: PlCategory) => (
						<Nav.Link
							as={Link}
							key={category.id}
							active={pageIndex == category.id}
							href={`${url}/pl-category/${category.id}`}
							style={{paddingLeft: '2.5rem'}}
						>
							{category.name}
						</Nav.Link>
					))}
					<Nav.Link
						as={Link}
						active={pageName === 'net-revenue-summary'}
						href={`${url}/net-revenue-summary`}
						style={{paddingLeft: '2.5rem'}}
					>
						Summary
					</Nav.Link>

					<hr className='border-dkBlueGrey' />
					<Nav.Link
						as={Link}
						className='h4 py-0 px-3 fw-bold'
						style={{fontSize: '1rem'}}
						active={pageName == 'total-expenses'}
						href={`${url}/total-expenses`}
					>
						Total Expenses
					</Nav.Link>
					{navPlCategories?.totalExpenses.map((category: PlCategory) => (
						<Nav.Link
							as={Link}
							key={category.id}
							active={pageIndex == category.id}
							href={`${url}/pl-category/${category.id}`}
							style={{paddingLeft: '2.5rem'}}
						>
							{category.name}
						</Nav.Link>
					))}
					<Nav.Link
						as={Link}
						active={pageName === 'total-expenses-summary'}
						href={`${url}/total-expenses-summary`}
						style={{paddingLeft: '2.5rem'}}
					>
						Summary
					</Nav.Link>

					<hr className='border-dkBlueGrey' />
					<span className='h4 fw-bold text-dkBlueGrey px-3'>
						Additional Information
					</span>
					{navPlCategories?.additionalInformation.map(
						(category: PlCategory) => (
							<Nav.Link
								as={Link}
								key={category.id}
								active={pageIndex == category.id}
								href={`${url}/pl-category/${category.id}`}
								style={{paddingLeft: '2.5rem'}}
							>
								{category.name}
							</Nav.Link>
						)
					)}

					<hr className='border-dkBlueGrey' />
					<Nav.Link
						as={Link}
						active={pageName === 'pl-summary'}
						href={`${url}/pl-summary`}
						className='h4 py-0 px-3 fw-bold'
						style={{fontSize: '1rem'}}
					>
						P&L Summary
					</Nav.Link>

					<hr className='border-dkBlueGrey' />
					<Nav.Link
						as={Link}
						active={pageName === 'finalize'}
						href={`${url}/finalize`}
						className='h4 py-0 px-3 fw-bold'
						style={{fontSize: '1rem'}}
					>
						Finalize
					</Nav.Link>
				</>
			)}
		</Nav>
		</div>
	);
};

export default SideNav;
